/**
 * Publish — the agent's marketplace storefront, step by step. The builder drafts the storefront (a
 * proposal); tests from the Tests tab count for publishing; the legal steps are the adviser's own clicks
 * (responsibility for what the agent says; a professional licence in regulated fields, uploaded on the
 * My AI Agents page; submitting for iMaven's review).
 *
 * API: GET /agent-builder/publishing · PUT /agents/:id/responsibility {accepted} · POST /agents/:id/submit|pause|resume
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../Context/ToastContext';
import { Box, Row, Help, Pill, PrimaryBtn, GhostBtn, Empty, errText } from './styles';

const STATE = { done: ['Done', 'rgba(63,185,80,0.18)', '#56d364'], todo: ['To do', 'rgba(210,153,34,0.18)', '#e3b341'], waiting: ['Waiting for iMaven', 'rgba(47,129,247,0.18)', '#79c0ff'], blocked: ['Blocked', 'rgba(248,81,73,0.18)', '#ff7b72'] };

export default function PublishPanel({ api, onAsk, onOpenTests }) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/agent-builder/publishing'); setData(r.data); } catch (e) { showToast(errText(e, 'Could not load the publishing status'), 'error'); }
  }, [api, showToast]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) showToast(ok, 'success'); await load(); } catch (e) { showToast(errText(e, 'That did not work'), 'error'); } finally { setBusy(false); }
  };

  if (!data) return <Empty>Loading…</Empty>;
  const sf = data.storefront;
  const step = (id) => data.steps.find(s => s.id === id);

  return (
    <div>
      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Publish your agent on the iMaven marketplace</div>
        <Help>Visitors of imavenai.com can then talk to your agent and be handed to you. Your agent keeps running on your own website either way.</Help>
      </Box>
      {data.steps.map(s => {
        const [label, bg, fg] = STATE[s.state] || [s.state];
        return (
          <Box key={s.id}>
            <Row style={{ justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div>
              <Pill $bg={bg} $fg={fg}>{label}</Pill>
            </Row>
            {s.detail && <Help>{s.detail}</Help>}
            {s.id === 'storefront' && (
              <Row style={{ marginTop: 8 }}>
                <GhostBtn disabled={busy} onClick={() => onAsk(sf ? 'Please improve my marketplace storefront (headline, description, persona, sample questions) from what you know about my practice.' : 'Please draft my marketplace storefront from what you know about my practice: name, headline, description, how the agent presents itself, and sample questions.')}>{sf ? 'Ask the builder to improve it' : 'Ask the builder to draft it'}</GhostBtn>
              </Row>
            )}
            {s.id === 'test' && s.state !== 'done' && <Row style={{ marginTop: 8 }}><GhostBtn onClick={onOpenTests}>Open Tests</GhostBtn></Row>}
            {s.id === 'responsibility' && s.state !== 'done' && sf && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', background: '#0b0f14', borderRadius: 8, padding: 10, maxHeight: 220, overflowY: 'auto' }}>{data.responsibilityText}</div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 8 }}>
                  <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} /> I have read this and I am responsible for the information my agent gives.
                </label>
                <Row style={{ marginTop: 8 }}>
                  <PrimaryBtn disabled={busy || !agree} onClick={() => act(() => api.put(`/agents/${sf.id}/responsibility`, { accepted: true }), 'Confirmed')}>Confirm</PrimaryBtn>
                </Row>
              </div>
            )}
            {s.id === 'licence' && s.state === 'todo' && <Row style={{ marginTop: 8 }}><GhostBtn onClick={() => navigate('/agent')}>Register the licence</GhostBtn></Row>}
          </Box>
        );
      })}
      {sf && (
        <Row style={{ marginTop: 4 }}>
          {data.canSubmit && <PrimaryBtn disabled={busy} onClick={() => act(() => api.post(`/agents/${sf.id}/submit`), 'Submitted for review — iMaven will check it.')}>Submit for review</PrimaryBtn>}
          {sf.status === 'published' && <GhostBtn disabled={busy} onClick={() => act(() => api.post(`/agents/${sf.id}/pause`), 'Paused')}>Pause listing</GhostBtn>}
          {sf.status === 'paused' && <GhostBtn disabled={busy} onClick={() => act(() => api.post(`/agents/${sf.id}/resume`), 'Resumed')}>Resume listing</GhostBtn>}
          <GhostBtn onClick={() => navigate('/agent')}>Storefront details & earnings</GhostBtn>
        </Row>
      )}
      {step('review')?.state === 'waiting' && <Help>iMaven reviews submissions; this page shows the decision when it is made.</Help>}
    </div>
  );
}
